import { useEffect, useMemo, useState } from "react";
import { getCampaignContext, getDeviceInfo } from "@/lib/analyticsClient";
import { resolveArcadeAccess, type ArcadeAccess } from "@/utils/arcade/playGate";
import { MAILING_EMAIL_CHANGED_EVENT, getSubscribedEmail, getUnlockToken } from "../../mailing/mailingStorage";
import { getArcadeBackend, useArcadeQuery } from "./arcadeBackend";

export interface ArcadeAccessState extends ArcadeAccess {
  storedEmail: string | null;
  unlockToken: string | null;
}

// Reads who is playing: signed in member, admin, an email on this device, or
// nobody yet. The decision itself is resolveArcadeAccess in playGate.ts.
export function useArcadeAccess(): ArcadeAccessState {
  const [storedEmail, setStoredEmail] = useState<string | null>(() => getSubscribedEmail());
  const [unlockToken, setUnlockToken] = useState<string | null>(() => getUnlockToken());

  useEffect(() => {
    const sync = () => {
      setStoredEmail(getSubscribedEmail());
      setUnlockToken(getUnlockToken());
    };
    window.addEventListener(MAILING_EMAIL_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(MAILING_EMAIL_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const backendReady = getArcadeBackend().ready;
  const viewer = useArcadeQuery("viewer", backendReady ? {} : "skip");
  const check = useArcadeQuery("checkUnlock", backendReady && storedEmail && unlockToken ? { email: storedEmail, token: unlockToken } : "skip");
  const context = useMemo(() => ({ isBot: getDeviceInfo().isBot, adSession: getCampaignContext().adModeSession }), []);

  const access = resolveArcadeAccess({
    backendReady,
    isBot: context.isBot,
    adSession: context.adSession,
    viewer: viewer.status === "ready" ? viewer.value : undefined,
    storedEmail,
    hasUnlockToken: Boolean(unlockToken),
    unlockCheck: check.status === "ready" ? check.value : undefined,
  });
  return { ...access, storedEmail, unlockToken };
}
